import { LookupId, Model, PModel, PropertyPath } from '../engine.js';
import { Option } from '../option.js';
import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import { ModelSystemProperties } from '../internal/ModelSystemProperties.js';
import { Schema } from '../schema.js';
import {
  LookupRecord,
  MultiPropertyLookupRecord, oneOptionLookupRecordTypeGuard, multiPropertiesLookupRecordTypeGuard,
  singlePropertyLookupTypeGuard, extendedSinglePropertyLookupTypeGuard, SinglePropertyLookupRecord, Variation
} from './ResolutionSchemaDefinition.js';
import { engineUtils } from '../services/EngineUtils.js';
import { TypeService } from '../services/TypeService.js';
import { ExplicitResolutionStrategy } from './ExplicitResolutionStrategy.js';

export type ResolutionSchemaId = string;
export type OptionPropertyName = string;
export type OptionPropertyNames = PropertyPath[][];
export type Reference = Map<PropertyPath, { [key: string]: any }> & { kind: 'Reference' };

export class ResolutionSchema {
  constructor(
    public readonly lookups: LookupRecord[]
  ) {
  }

  /**
   * Returns distinct list of option propertyPaths of fixed lookups
   * @param resolutionSchema
   */
  topOptions(lookupId: LookupId): PropertyPath[] {
    const result = new Set<string>();
    const lookup = this.getLookupById(lookupId);
    if (!lookup) {
      return [];
    }

    if (oneOptionLookupRecordTypeGuard(lookup)) {
      const ids = lookup.fixed;
      for (const id of ids) {
        let fixedLookup = this.getLookupById(id);
        if (fixedLookup) {
          result.add(fixedLookup.option);
        }
      }
    } else {
      throw new Error('Record type Not supported');
    }
    return Array.from(result);
  }

  variationOptions(lookupId: LookupId): PropertyPath[] {
    const result = new Set<PropertyPath>();
    const lookup = this.getLookupById(lookupId);
    for (const variation of lookup.variation) {
      const lookupIds = Array.isArray(variation) ? variation : [variation];
      for (const id of lookupIds) {
        let bottomLookup = this.getLookupById(id);
        if (bottomLookup) {
          result.add(bottomLookup.option);
        }
      }
    }
    return Array.from(result);
  }

  /**
   * Returns list of OptionFilters that is build for all options that is declared before propertyPath in ResolutionSchema
   *
   * Example:
   *  door.name
   *  door.material
   *  frame.name
   * *frame.material
   *  frame.width
   *
   * requiredFiltersFor('frame.material') => [OptionFilter('door', ['name', 'material']), OptionFilter('frame', ['name'])]
   *
   */
  topFiltersFor(lookupId: LookupId, filterGroup: string): OptionFilter[] {
    const optionProperties = new Map<PropertyPath, PropertyPath[][]>();
    for (const lookup of this.lookups) {
      // filters apply in the same filter group only
      if (lookup.filterGroup !== filterGroup) {
        continue;
      }
      if (oneOptionLookupRecordTypeGuard(lookup)) {
        if (lookup.id === lookupId) {
          return this.createOptionFilters(optionProperties);
        } else {
          if (!optionProperties.get(lookup.option)) {
            optionProperties.set(lookup.option, []);
          }
          if (singlePropertyLookupTypeGuard(lookup)) {
            optionProperties.get(lookup.option).push([lookup.property]);
          } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            const properties = [lookup.properties[lookup.key]];
            optionProperties.get(lookup.option).push(properties);
          } else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
            optionProperties.get(lookup.option).push(Object.keys(lookup.properties));
          }
        }
      } else {
        throw new Error('Record type Not supported');
      }
    }
    return [];
  }

  private createOptionFilters(optionProperties: Map<PropertyPath, PropertyPath[][]>) {
    const result: OptionFilter[] = [];
    for (let [optionPath, propertyNames] of optionProperties) {
      result.push(new OptionFilter(optionPath, propertyNames))
    }
    return result;
  }


  allOptions(): PropertyPath[] {
    const paths = new Set<PropertyPath>();
    for (const lookup of this.lookups) {
      if (oneOptionLookupRecordTypeGuard(lookup)) {
        paths.add(lookup.option)
      } else {
        throw new Error('Record type Not supported');
      }
    }
    return Array.from(paths);
  }

  getReference(lookupId: LookupId, value: any, model: any, optionsToUse: Set<PropertyPath>): Reference {
    const map = new Map<PropertyPath, { [key: string]: any }>();
    const lookup = this.safelyGetLookupById(lookupId);
    const visited = new Set<LookupId>();
    for (const variation of lookup.variation) {
      const lookupIds = !Array.isArray(variation) ? [variation] : variation;
      for (const id of lookupIds) {
        if (visited.has(id)) {
          continue;
        }
        const variantLookup = this.safelyGetLookupById(id);
        visited.add(id);
        const option = variantLookup.option
        if (!optionsToUse.has(option)) {
          continue;
        }
        let properties = map.get(option);
        if (!properties) {
          properties = {};
          map.set(option, properties);
        }
        if (singlePropertyLookupTypeGuard(variantLookup)) {
          properties[variantLookup.property] = engineUtils.getPropertyPathValue(
            model,
            TypeService.joinPath(variantLookup.option, variantLookup.property)
          );
        } else if (extendedSinglePropertyLookupTypeGuard(variantLookup)) {
          const property = variantLookup.properties[variantLookup.key];
          properties[property] = engineUtils.getPropertyPathValue(
            model,
            TypeService.joinPath(variantLookup.option, property)
          );
        } else if (multiPropertiesLookupRecordTypeGuard(variantLookup)) {
          //TODO: Check
          console.warn('MultiProperty lookup mutator is an experimental feature')
          const lookupProperties = Object.values(variantLookup.properties);
          for (const property of lookupProperties) {
            properties[property] = engineUtils.getPropertyPathValue(
              model,
              TypeService.joinPath(variantLookup.option, property)
            );
          }
        }
         else {
          throw new Error('Not supported');
        }
      }
    }

    // Overrides current lookup with new value
    let properties = map.get(lookup.option);
    if (!properties) {
      properties = {};
      map.set(lookup.option, properties);
    }
    if (singlePropertyLookupTypeGuard(lookup)) {
      properties[lookup.property] = value;
    } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
      const property = lookup.properties[lookup.key];
      properties[property] = value[lookup.key];
    } else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
      //TODO: Check
      const lookupProperties = Object.values(lookup.properties);
      for (const property of lookupProperties) {
        properties[property] = value[property];
      }
    } else {
      throw new Error('Not supported');
    }
    return map as Reference;
  }

  orderOptionValues(lookupId: LookupId, model: PModel, lookupValue: any, optionsPotentialValues: Map<PropertyPath, Option[]>): Generator<[PropertyPath, Option[]][]> {
    const lookup = this.getLookupById(lookupId);
    const options = new Set<PropertyPath>(optionsPotentialValues.keys());
    const reference = this.getReference(lookupId, lookupValue, model, options);
    const strategy = new ExplicitResolutionStrategy(lookup, this, reference);
    return strategy.orderOptionValues(model, optionsPotentialValues);
  }

  getAllOptionResolutionProperties(optionPath: PropertyPath): PropertyPath[] {
    const result: PropertyPath[] = [];
    for (let record of this.lookups) {
      if (singlePropertyLookupTypeGuard(record)) {
        if (record.option === optionPath) {
          result.push(record.property);
        }
      } else if (extendedSinglePropertyLookupTypeGuard(record)) {
        if (record.option === optionPath) {
          result.push(record.properties[record.key])
        }
      } else if (multiPropertiesLookupRecordTypeGuard(record)) {
        if (record.option === optionPath) {
          result.push(...Object.values(record.properties));
        }
      }
    }
    return result;
  }

  getLookupById(lookupId: LookupId): LookupRecord | undefined {
    for (const record of this.lookups) {
      if (record.id === lookupId) {
        if (oneOptionLookupRecordTypeGuard(record)) {
          return record;
        }
      }
    }
    return undefined;
  }

  safelyGetLookupById(id: LookupId) {
    const lookup = this.getLookupById(id);
    if (!lookup) {
      throw new Error(`Cannot find lookup with id ${id}`);
    }
    return lookup;
  }

  getVariationLookups(variation: Variation) {
    const result: LookupRecord[] = [];
    for (const variant of variation) {
      const ids = Array.isArray(variant) ? variant : [variant];
      for (const id of ids) {
        result.push(this.safelyGetLookupById(id));
      }
    }
    return result;

  }
}

export function order(options: { model: any }[], properties: PropertyPath[], model: any, path: PropertyPath) {
  let nextToOrdered = 0;
  while (properties.length) {

    const property = properties.shift();
    const propertyPath = TypeService.joinPath(path, property);
    const value = engineUtils.getPropertyPathValue(model, propertyPath);
    for (let i = nextToOrdered; i < options.length; i++) {
      const optionValue = engineUtils.getPropertyPathValue(options[i].model, property)
      if (engineUtils.equals(optionValue, value)) {
        const tmp = options[nextToOrdered];
        options[nextToOrdered] = options[i];
        options[i] = tmp;
        nextToOrdered++;
      }
    }
  }
}

export class OptionFilter {
  constructor(
    public readonly optionPropertyPath: PropertyPath,
    private readonly properties: OptionPropertyNames) {
  }

  match(schema: Schema, model: Model, option: Option): boolean {
    return this.properties.every(properties => {
      const typeRef = ModelSystemProperties.getTypeRef(model);
      const value = new PropertyPathWalker(this.optionPropertyPath, schema, model, typeRef).getPropertyValue().value;
      //TODO: Add validation etc
      const left = engineUtils.getPropertyPathValues(value, properties);
      const right = engineUtils.getPropertyPathValues(option.model, properties);
      return engineUtils.equals(left, right);
    })
  }
}

